import React, { FC, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { getTravelDistance } from '@shared/utils/distanceInMiles';
import { BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { WalkIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Car } from '@withjoy/joykit/icons';
import { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { extractTypedDistances } from '@shared/utils/formatting/distance.formatter';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { label2ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';

interface DistanceStatisticsProps extends BoxProps {
  distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
  milesToVenue?: number | null;
  useShortWording?: boolean;
}

export const DistanceStatistics: FC<DistanceStatisticsProps> = ({ distances, milesToVenue, useShortWording = false, ...props }) => {
  const { tq } = useTranslation('guestSiteCustom');

  const formatTravelDistance = useFormatTravelDistance();

  const [accommodationsTravelDistanceExperimentEnabled] = useIsFeatureEnabled('accommodationsTravelDistanceExperiment');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');

  const distanceInfo = useMemo(() => {
    // Check for distances to enable only for guest and admin accommodations
    if (distances && accommodationsTravelDistanceExperimentEnabled) {
      const { isWalkable, displayDistance } = extractTypedDistances(distances?.commonDistance);

      if (!displayDistance?.durationMinutes) return null;

      return { isWalkable, minutes: displayDistance.durationMinutes };
    }

    if (!milesToVenue) return null;

    const travelDistance = getTravelDistance(milesToVenue);

    if (travelDistance.minutes <= 0) return null;

    return { isWalkable: travelDistance.isWalkableDistance, minutes: travelDistance.minutes };
  }, [accommodationsTravelDistanceExperimentEnabled, distances, milesToVenue]);

  if (!distanceInfo) return null;

  const { formatted, unitLabel } = formatTravelDistance({ value: distanceInfo.minutes, type: 'time' });
  const displayDistance = `${formatted} ${unitLabel}`;

  // Get display travel walking distance in minutes
  const displayWalkableDistance = useShortWording ? tq`${displayDistance}` : tq`${displayDistance} walk`;

  // Get display travel driving distance in minutes
  const displayDriveDistance = useShortWording ? tq`${displayDistance}` : tq`${displayDistance} drive`;

  return (
    <Flex
      width="fit-content"
      alignItems="center"
      {...props}
    >
      {distanceInfo.isWalkable ? (
        <WalkIcon color={accommodationGuestHotelTileFontUpdatesEnabled ? 'mono10' : 'mono14'} />
      ) : (
        <Car
          size={16}
          color={accommodationGuestHotelTileFontUpdatesEnabled ? 'mono10' : 'mono14'}
        />
      )}

      {accommodationGuestHotelTileFontUpdatesEnabled ? (
        <TextV2
          typographyVariant="label2"
          fontFamily={'Inter UI'}
          color="mono10"
          lineHeight={1.5}
          marginLeft={1}
          fontSize={props.fontSize ?? 13}
          maxWidth={{ _: pxToRem(180), sm: pxToRem(260) }}
          truncate
        >
          {distanceInfo.isWalkable ? displayWalkableDistance : displayDriveDistance}
        </TextV2>
      ) : (
        <GuestSiteTypographyOverride
          override={label2ToParagraphOverride}
          defaultFontFamily="Inter UI"
        >
          <TextV2
            {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
            fontSize={props.fontSize ?? 14}
            color="mono14"
            lineHeight={1.5}
            marginLeft={1}
            maxWidth={{ _: pxToRem(180), sm: pxToRem(260) }}
            truncate
          >
            {distanceInfo.isWalkable ? displayWalkableDistance : displayDriveDistance}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
    </Flex>
  );
};
